import { LegalLayout, Section, LEGAL } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      testid="terms-page"
      title="Terms of Service"
      path="/terms"
      seoTitle="Terms of Service | Food Recall Alerts Australia"
      seoDescription="Terms of Service for Food Recall Alerts, the independent Australian food recall SMS service operated by Seamless Apps. Covers SMS consent, subscriptions, refunds and limitations."
      intro="These terms explain what Food Recall Alerts does, what it does not do, and what you agree to when you use it. Please read them before turning on alerts."
    >
      <Section n={1} title="What the service is">
        <p>
          Food Recall Alerts monitors food recall notices published by Food Standards Australia New
          Zealand (FSANZ) and sends you a short SMS when a recall matches the preferences you have
          chosen, with a link to the official notice.
        </p>
        <p>
          <strong>We are not affiliated with, endorsed by, or acting on behalf of FSANZ</strong> or any
          Australian government agency. We are an independent notification service. FSANZ remains the
          only authoritative source and its published notice always prevails over our SMS.
        </p>
      </Section>

      <Section n={2} title="Eligibility and your account">
        <p>
          You must be at least 16 years old and use an Australian mobile number that you own or are
          authorised to use. Your account is identified by that number and is verified by a one-time
          code. Do not sign up someone else's number without their consent.
        </p>
      </Section>

      <Section n={3} title="SMS consent (Spam Act 2003)">
        <p>
          By creating an account you consent to receiving SMS messages from us: a verification code, a
          welcome message and example alert, subscription and referral notices, and recall alerts that
          match your preferences. Message frequency depends on how many recalls match you.
        </p>
        <p>
          You can withdraw consent at any time by replying <strong>STOP</strong> to any message, turning
          SMS off in your settings, or emailing {LEGAL.email}. Standard carrier charges may apply to
          messages you receive.
        </p>
      </Section>

      <Section n={4} title="Subscriptions, pricing and billing">
        <p>
          Browsing recall history on the website is free. SMS alerts require a paid subscription:
          A$4.99 per month or A$39.99 per year. Prices are in Australian dollars and are the total you
          pay - the price shown is GST inclusive where GST applies.
        </p>
        <p>
          Payments are processed by Stripe. Subscriptions renew automatically at the end of each billing
          period until cancelled. You can cancel any time from your settings; access continues until the
          end of the period you have paid for.
        </p>
      </Section>

      <Section n={5} title="Refunds">
        <p>
          If you are not happy, email {LEGAL.email} within <strong>48 hours of your most recent billing
          date</strong> and we will refund that payment in full. Outside that window, payments for a
          period already begun are not refundable.
        </p>
        <p>
          Nothing in these terms limits your rights under the Australian Consumer Law, including the
          consumer guarantees. If the service is not provided with due care and skill, you may be
          entitled to a remedy that cannot be excluded.
        </p>
      </Section>

      <Section n={6} title="Referral credits">
        <p>
          When someone you refer completes signup, we apply a credit that skips one billing cycle on
          your subscription. Credits are applied one at a time, have no cash value, cannot be
          transferred, and may be withheld where we reasonably believe the referral is not genuine
          (for example self-referrals or automated signups).
        </p>
      </Section>

      <Section n={7} title="Important limitations - please read">
        <p>
          This is a notification service, not a safety guarantee. We rely on FSANZ publishing a notice
          and on your mobile carrier delivering our message. Alerts may be delayed, incomplete or not
          delivered because of feed outages, network issues, an unreachable handset, or a recall that
          does not match your chosen filters.
        </p>
        <p>
          Do not rely on this service as your only safeguard. Always read product labels, follow medical
          advice about allergies, and check foodstandards.gov.au directly if you are unsure. Nothing we
          send is medical advice.
        </p>
      </Section>

      <Section n={8} title="Acceptable use">
        <p>
          Do not scrape, resell or redistribute our alerts commercially, attempt to disrupt the service,
          or use it to send unsolicited messages. We may suspend an account that breaches these terms or
          is used unlawfully.
        </p>
      </Section>

      <Section n={9} title="Liability">
        <p>
          To the maximum extent permitted by law, and other than liability that cannot be excluded under
          the Australian Consumer Law, our total liability to you for any claim connected with the
          service is limited to the amount you paid us in the 12 months before the claim. We are not
          liable for indirect or consequential loss.
        </p>
      </Section>

      <Section n={10} title="Changes and termination">
        <p>
          We may change these terms or the service. If a change is material we will tell you by SMS or
          on the website before it takes effect; continuing to use the service means you accept it. You
          may stop using the service and delete your account at any time from your settings.
        </p>
      </Section>

      <Section n={11} title="Governing law">
        <p>
          These terms are governed by the laws of {LEGAL.jurisdiction}, and you and we submit to the
          non-exclusive jurisdiction of the courts of that state.
        </p>
      </Section>
    </LegalLayout>
  );
}
